import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Empty from 'antd/lib/empty';
import Form from 'antd/lib/form';
import Layout from 'antd/lib/layout';
import Modal from 'antd/lib/modal';
import Popover from 'antd/lib/popover';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import Table from 'antd/lib/table';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { ColumnsType } from 'antd/lib/table';
import { InfoCircleOutlined, PartitionOutlined, PlusOutlined, WarningFilled } from '@ant-design/icons';
import * as API from '@/api';
import { AppPlatform, ReleaseNav } from '@/types';
import {
    IosDependenciesConfig,
    IosDependenciesDependencyReport,
    IosDependenciesFileOptionsResponse,
    IosDependenciesReleaseResponse,
} from '@/types/iosDependencies';
import { Destination } from '@/types/destinations';
import { RootState } from '@/reducers/reducers';
import {
    selectAppSettings,
    selectAuthCredentials,
    selectDestinationInfoByDestinationStepConfigId,
    selectIsReleaseCompleted,
    selectIsReleaseKickedOff,
} from '@/selectors/selectors';
import { selectIosDependenciesForRelease } from '@/reducers/iosDependencies';
import {
    TRY_FETCH_IOS_DEPENDENCIES_FOR_RELEASE,
    tryFetchIosDependenciesForRelease,
    updateIosDependenciesForRelease,
} from '@/actions/iosDependencies';
import { getIsApplePlatform, getIsLoading } from '@/helpers';
import { ReleaseComponentProps } from '@/typescriptHelpers';
import useSetLastViewedReleaseStep from '@/components/hooks/useSetLastViewedReleaseStep';
import DependencyDetailContent from '@/components/iosDependencies/DependencyDetailPopover';
import { antGrey, rwColors, titleStyle } from '@/components/styles/globalStyles';
import { globalMessage } from '@/utils/globalMessage';

interface ConfigureModalValues {
    destinationId?: string;
    pbxprojPath: string;
    packageResolvedPath: string;
    xcconfigPaths?: string[];
}

import {
    buildDestinationHeaderLabels,
    compareVersionStrings,
    countUpdates,
    formatConstraint,
    formatMinIOS,
    formatStatusLabel,
    isIndirect,
    pluralize,
    statusTagColor,
    totalDeps,
} from '@/components/iosDependencies/iosDependenciesUtils';

const makeDependencyColumns = (appMinIOSVersion?: string): ColumnsType<IosDependenciesDependencyReport> => [
    {
        title: 'Package name',
        dataIndex: 'identity',
        key: 'identity',
        width: 380,
        ellipsis: true,
        sorter: (a, b) => a.identity.localeCompare(b.identity),
        render: (identity: string, row) => (
            <Popover
                content={<DependencyDetailContent row={row} appMinIOSVersion={appMinIOSVersion} />}
                trigger="hover"
                placement="right"
                mouseEnterDelay={0.2}
            >
                <Space direction="vertical" size={0} style={{ cursor: 'help', maxWidth: '100%' }}>
                    <Text strong ellipsis>
                        {identity}
                        {isIndirect(row) && row.dependencyGraph && row.dependencyGraph[0] !== 'unknown' && (
                            <Text style={{ fontWeight: 'normal', color: antGrey.grey6 }}>
                                {` (${row.dependencyGraph.join(' → ')})`}
                            </Text>
                        )}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                        {row.location}
                    </Text>
                </Space>
            </Popover>
        ),
    },
    {
        title: 'Source',
        key: 'source',
        width: 110,
        sorter: (a, b) => {
            const aIndirect = isIndirect(a);
            const bIndirect = isIndirect(b);
            if (aIndirect === bIndirect) return 0;
            return aIndirect ? 1 : -1;
        },
        filters: [
            { text: 'Direct', value: 'direct' },
            { text: 'Indirect', value: 'indirect' },
        ],
        onFilter: (value, row) => (value === 'indirect' ? isIndirect(row) : !isIndirect(row)),
        render: (_, row) => (isIndirect(row) ? <Tag>Indirect</Tag> : <Tag color="magenta">Direct</Tag>),
    },
    {
        title: 'Dependency rule',
        key: 'pinnedAs',
        width: 180,
        sorter: (a, b) => {
            const aKind = a.versionConstraint?.kind ?? '';
            const bKind = b.versionConstraint?.kind ?? '';
            if (!aKind && !bKind) return 0;
            if (!aKind) return 1;
            if (!bKind) return -1;
            return aKind.localeCompare(bKind);
        },
        render: (_, row) => (row.versionConstraint ? <Text code>{formatConstraint(row.versionConstraint)}</Text> : '-'),
    },
    {
        title: 'Latest version requires',
        dataIndex: 'latestCompatibleMinIOS',
        key: 'latestCompatibleMinIOS',
        width: 130,
        sorter: (a, b, sortOrder) => {
            const aVal = (a.latestCompatibleMinIOS || a.latestMinIOS || '').trim();
            const bVal = (b.latestCompatibleMinIOS || b.latestMinIOS || '').trim();
            if (!aVal && !bVal) return 0;
            if (!aVal) return sortOrder === 'descend' ? -1 : 1;
            if (!bVal) return sortOrder === 'descend' ? 1 : -1;
            return compareVersionStrings(aVal, bVal);
        },
        render: (value: string, row) => {
            const shown = value || row.latestMinIOS;
            if (!shown) return '-';
            const shownExceeds = !value && row.latestMinIOSExceedsAppMin;
            if (!shownExceeds) return formatMinIOS(shown);
            const tooltip =
                appMinIOSVersion ?
                    `Requires iOS ${shown} but the app targets iOS ${appMinIOSVersion}`
                :   `Requires iOS ${shown}, which is higher than the app's minimum supported iOS version`;
            return (
                <Space size={4}>
                    <Text style={{ color: rwColors.statusYellow }}>{formatMinIOS(shown)}</Text>
                    <Tooltip title={tooltip}>
                        <WarningFilled style={{ color: rwColors.statusYellow }} />
                    </Tooltip>
                </Space>
            );
        },
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 160,
        sorter: (a, b) => a.status.localeCompare(b.status),
        filters: [
            { text: 'Up to date', value: 'up-to-date' },
            { text: 'Update available', value: 'update-available' },
            { text: 'Not checked', value: 'not-checked' },
            { text: 'No current version', value: 'no-current-version' },
        ],
        onFilter: (value, row) => row.status === value,
        render: (value: string, row) => (
            <Space direction="vertical" size={0}>
                <Tag color={statusTagColor(value)}>{formatStatusLabel(value)}</Tag>
                {row.checkError && (
                    <Text type="danger" style={{ fontSize: 12 }}>
                        {row.checkError}
                    </Text>
                )}
            </Space>
        ),
    },
    {
        title: 'Current',
        dataIndex: 'currentVersion',
        key: 'currentVersion',
        width: 110,
        render: (value: string, row) => value || row.revision || '-',
    },
    {
        title: 'Latest compatible',
        dataIndex: 'latestCompatibleVersion',
        key: 'latestCompatibleVersion',
        width: 140,
        render: (value: string) => value || '-',
    },
    {
        title: 'Latest',
        dataIndex: 'latestVersion',
        key: 'latestVersion',
        width: 110,
        render: (value: string) => value || '-',
    },
];

const destinationOptions = (destinations: Destination[]) =>
    destinations.map((destination) => ({
        label: destination.name,
        value: destination.destinationId ?? destination.stepConfigId,
    }));

const pathOptions = (paths: string[]) => paths.map((path) => ({ label: path, value: path }));

const ConfigureIosDependenciesModal: React.FC<{
    open: boolean;
    orgId: string;
    appId: string;
    releaseId: string;
    destinations: Destination[];
    initialDestinationId?: string;
    existingConfigs: Record<string, IosDependenciesConfig>;
    onConfigured: (response: IosDependenciesReleaseResponse) => void;
    onClose: () => void;
}> = ({
    open,
    orgId,
    appId,
    releaseId,
    destinations,
    initialDestinationId,
    existingConfigs,
    onConfigured,
    onClose,
}) => {
    const [form] = Form.useForm<ConfigureModalValues>();
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const [fileOptions, setFileOptions] = React.useState<IosDependenciesFileOptionsResponse | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);

    React.useEffect(() => {
        if (!open || !auth) {
            return;
        }

        setIsLoading(true);
        API.getIosDependenciesFileOptions({ orgId, appId, releaseId }, auth)
            .then(setFileOptions)
            .catch((err) => globalMessage.error(err.message))
            .finally(() => setIsLoading(false));
    }, [open, auth, orgId, appId, releaseId]);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        const destinationId =
            initialDestinationId ??
            (destinations.length === 1 ? destinationOptions(destinations)[0]?.value : undefined);
        const existing = destinationId ? existingConfigs[destinationId] : undefined;
        form.setFieldsValue({
            destinationId,
            pbxprojPath: existing?.pbxprojPath,
            packageResolvedPath: existing?.packageResolvedPath,
            xcconfigPaths: existing?.xcconfigPaths ?? [],
        });
    }, [open, initialDestinationId, destinations, existingConfigs, form]);

    const handleOk = () => {
        form.validateFields().then((values) => {
            const destinationId =
                values.destinationId ?? initialDestinationId ?? destinationOptions(destinations)[0]?.value;
            if (!auth || !destinationId) {
                return;
            }

            setIsSaving(true);
            API.saveIosDependenciesConfig(
                {
                    orgId,
                    appId,
                    releaseId,
                    destinationId,
                    pbxprojPath: values.pbxprojPath,
                    packageResolvedPath: values.packageResolvedPath,
                    xcconfigPaths: values.xcconfigPaths ?? [],
                    shouldIncludeReposOwnedByCustomer: false,
                },
                auth,
            )
                .then((response) => {
                    onConfigured(response);
                    globalMessage.success('iOS dependency configuration saved');
                    onClose();
                })
                .catch((err) => globalMessage.error(err.message))
                .finally(() => setIsSaving(false));
        });
    };

    return (
        <Modal
            open={open}
            title="Configure dependencies"
            okText="Save configuration"
            onOk={handleOk}
            onCancel={onClose}
            confirmLoading={isSaving}
            width={720}
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {fileOptions?.branch && <Alert type="info" message={`Showing files from ${fileOptions.branch}`} />}
                <Spin spinning={isLoading} tip="Loading files from the release branch…">
                    <Form form={form} layout="vertical">
                        {destinations.length > 1 && (
                            <Form.Item
                                label="Destination"
                                name="destinationId"
                                rules={[{ required: true, message: 'Choose a destination' }]}
                            >
                                <Select
                                    disabled={!!initialDestinationId}
                                    options={destinationOptions(destinations)}
                                    placeholder="Choose destination"
                                />
                            </Form.Item>
                        )}
                        <Form.Item
                            label="project.pbxproj"
                            name="pbxprojPath"
                            rules={[{ required: true, message: 'Choose a pbxproj file' }]}
                        >
                            <Select
                                showSearch
                                disabled={isLoading}
                                options={pathOptions(fileOptions?.pbxprojPaths ?? [])}
                                placeholder="Choose project.pbxproj"
                            />
                        </Form.Item>
                        <Form.Item
                            label="Package.resolved"
                            name="packageResolvedPath"
                            rules={[{ required: true, message: 'Choose a Package.resolved file' }]}
                        >
                            <Select
                                showSearch
                                disabled={isLoading}
                                options={pathOptions(fileOptions?.packageResolvedPaths ?? [])}
                                placeholder="Choose Package.resolved"
                            />
                        </Form.Item>
                        <Form.Item label=".xcconfig files" name="xcconfigPaths">
                            <Select
                                mode="multiple"
                                showSearch
                                disabled={isLoading}
                                options={pathOptions(fileOptions?.xcconfigPaths ?? [])}
                                placeholder="Choose xcconfig files"
                            />
                        </Form.Item>
                        {!!fileOptions?.xcconfigPaths?.length && (
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                onClick={() => form.setFieldsValue({ xcconfigPaths: fileOptions.xcconfigPaths })}
                            >
                                Select all xcconfig files
                            </Button>
                        )}
                    </Form>
                </Spin>
            </Space>
        </Modal>
    );
};

const IosDependenciesPage: React.FC<ReleaseComponentProps> = ({ orgId, appId, releaseId }) => {
    useSetLastViewedReleaseStep(releaseId, ReleaseNav.IosDependencies);

    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const appSettings = useSelector((state: RootState) => selectAppSettings(state, appId));
    const isReleaseCompleted = useSelector((state: RootState) => selectIsReleaseCompleted(state, releaseId));
    const isReleaseKickedOff = useSelector((state: RootState) => selectIsReleaseKickedOff(state, releaseId));
    const configureDisabled = isReleaseCompleted || !isReleaseKickedOff;
    const configureDisabledTooltip =
        isReleaseCompleted ? 'This release is completed — dependencies cannot be updated'
        : !isReleaseKickedOff ? 'Kick off this release before configuring dependencies'
        : '';
    const updateDisabledTooltip =
        configureDisabled ? 'Update the configuration in a kicked off uncompleted release' : '';
    const destinationsByStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );
    const destinations = React.useMemo(
        () =>
            Object.values(destinationsByStepConfigId).filter(
                (destination) =>
                    getIsApplePlatform(destination.platform) || destination.platform === AppPlatform.IosSDK,
            ),
        [destinationsByStepConfigId],
    );
    const data = useSelector((state: RootState) => selectIosDependenciesForRelease(state, releaseId));
    const isLoading = useSelector((state: RootState) => getIsLoading(TRY_FETCH_IOS_DEPENDENCIES_FOR_RELEASE, state));
    const [modalDestinationId, setModalDestinationId] = React.useState<string | undefined>();
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const configs: Record<string, IosDependenciesConfig> = React.useMemo(
        () => appSettings?.iosDependenciesConfigs ?? {},
        [appSettings?.iosDependenciesConfigs],
    );
    const configuredDestinationIds = React.useMemo(
        () => new Set([...Object.keys(configs), ...(data?.reports ?? []).map((report) => report.destinationId)]),
        [configs, data?.reports],
    );
    const unconfiguredDestinations = destinations.filter((destination) => {
        const destinationId = destination.destinationId ?? destination.stepConfigId;
        return !configuredDestinationIds.has(destinationId);
    });

    React.useEffect(() => {
        document.title = 'Dependencies';
    }, []);

    React.useEffect(() => {
        if (!auth) {
            return;
        }
        dispatch(tryFetchIosDependenciesForRelease({ orgId, appId, releaseId }));
    }, [dispatch, auth, orgId, appId, releaseId]);

    const handleConfigured = React.useCallback(
        (response: IosDependenciesReleaseResponse) => {
            dispatch(updateIosDependenciesForRelease({ releaseId, response }));
        },
        [dispatch, releaseId],
    );

    const openModal = (destinationId?: string) => {
        setModalDestinationId(destinationId);
        setIsModalOpen(true);
    };

    const modalDestinations =
        modalDestinationId ?
            destinations.filter(
                (destination) => (destination.destinationId ?? destination.stepConfigId) === modalDestinationId,
            )
        : unconfiguredDestinations.length > 0 ? unconfiguredDestinations
        : destinations;

    return (
        <Layout.Content style={{ padding: 24, overflow: 'auto' }}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Space direction="vertical" size={4}>
                        <Text style={titleStyle} type="secondary">
                            Dependencies
                        </Text>
                        <Text>Dependency reports by destination</Text>
                    </Space>
                    {data?.hasConfigs && unconfiguredDestinations.length > 0 && (
                        <Tooltip title={configureDisabledTooltip}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                disabled={configureDisabled}
                                onClick={() => openModal()}
                            >
                                Add destination
                            </Button>
                        </Tooltip>
                    )}
                </Space>

                {!appSettings?.isWorkflowsMigratedAndEnabled || destinations.length === 0 ?
                    <Alert
                        type="warning"
                        message="Dependencies is only available for Flightpaths apps with at least one Apple destination."
                    />
                : !data && isLoading ?
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                        <Spin />
                    </div>
                : !data?.hasConfigs ?
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No dependency configuration yet">
                        <Tooltip title={configureDisabledTooltip}>
                            <Button
                                type="primary"
                                icon={<PartitionOutlined />}
                                disabled={configureDisabled}
                                onClick={() => openModal()}
                            >
                                Configure dependencies
                            </Button>
                        </Tooltip>
                    </Empty>
                :   (() => {
                        const defaultActiveKeys =
                            data.reports.length === 1 ?
                                [data.reports[0].destinationId]
                            :   data.reports.filter((r) => countUpdates(r.report) > 0).map((r) => r.destinationId);
                        const destinationByReportId = new Map<string, (typeof destinations)[number]>();
                        for (const d of destinations) {
                            const id = d.destinationId ?? d.stepConfigId;
                            if (id) destinationByReportId.set(id, d);
                        }
                        const headerLabels = buildDestinationHeaderLabels(
                            data.reports.map((r) => {
                                const dest = destinationByReportId.get(r.destinationId);
                                return {
                                    destinationId: r.destinationId,
                                    destinationName: r.destinationName || dest?.name,
                                    platform: dest?.platform,
                                    appStoreType: dest?.appStoreType,
                                };
                            }),
                        );

                        return (
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                <Collapse defaultActiveKey={defaultActiveKeys}>
                                    {data.reports.map((destinationReport) => {
                                        const updates = countUpdates(destinationReport.report);
                                        const total = totalDeps(destinationReport.report);
                                        const summary =
                                            destinationReport.status === 'parsing' ? 'Parsing…'
                                            : destinationReport.status === 'failed' ? 'Parsing failed'
                                            : destinationReport.report ?
                                                `${updates} ${pluralize(updates, 'update', 'updates')} available across ${total} ${pluralize(total, 'dependency', 'dependencies')}`
                                            :   'No report available';
                                        const showConfigureButton = destinationReport.status !== 'parsing';
                                        return (
                                            <Collapse.Panel
                                                key={destinationReport.destinationId}
                                                header={
                                                    <Space size={6}>
                                                        <Text strong>
                                                            {headerLabels[destinationReport.destinationId]}:
                                                        </Text>
                                                        <Text type="secondary">{summary}</Text>
                                                    </Space>
                                                }
                                                extra={
                                                    showConfigureButton ?
                                                        <Tooltip title={updateDisabledTooltip}>
                                                            <Button
                                                                size="small"
                                                                icon={<PlusOutlined />}
                                                                disabled={configureDisabled}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openModal(destinationReport.destinationId);
                                                                }}
                                                            >
                                                                Configure
                                                            </Button>
                                                        </Tooltip>
                                                    :   null
                                                }
                                            >
                                                {destinationReport.status === 'parsing' ?
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            padding: 32,
                                                        }}
                                                    >
                                                        <Spin tip="Parsing dependencies…" />
                                                    </div>
                                                : destinationReport.status === 'failed' ?
                                                    <Alert
                                                        type="error"
                                                        message={`Dependencies parsing failed: ${destinationReport.errorMessage || 'unknown error'}`}
                                                    />
                                                : destinationReport.report ?
                                                    (() => {
                                                        const cfg = configs[destinationReport.destinationId];
                                                        const files: { label: string; path: string }[] = [];
                                                        if (destinationReport.report.pbxprojPath) {
                                                            files.push({
                                                                label: 'project.pbxproj',
                                                                path: destinationReport.report.pbxprojPath,
                                                            });
                                                        }
                                                        if (destinationReport.report.packageResolvedPath) {
                                                            files.push({
                                                                label: 'Package.resolved',
                                                                path: destinationReport.report.packageResolvedPath,
                                                            });
                                                        }
                                                        for (const p of cfg?.xcconfigPaths ?? []) {
                                                            if (p) files.push({ label: 'xcconfig', path: p });
                                                        }
                                                        const filesPopover = (
                                                            <Space
                                                                direction="vertical"
                                                                size={8}
                                                                style={{ maxWidth: 480 }}
                                                            >
                                                                <Text strong>Files used</Text>
                                                                <Space direction="vertical" size={4}>
                                                                    {files.map((f) => (
                                                                        <Space
                                                                            key={f.path}
                                                                            direction="vertical"
                                                                            size={0}
                                                                        >
                                                                            <Text
                                                                                type="secondary"
                                                                                style={{ fontSize: 12 }}
                                                                            >
                                                                                {f.label}
                                                                            </Text>
                                                                            <Text
                                                                                copyable={{ text: f.path }}
                                                                                style={{
                                                                                    fontSize: 12,
                                                                                    wordBreak: 'break-all',
                                                                                }}
                                                                            >
                                                                                {f.path}
                                                                            </Text>
                                                                        </Space>
                                                                    ))}
                                                                </Space>
                                                            </Space>
                                                        );
                                                        return (
                                                            <Space
                                                                direction="vertical"
                                                                size={12}
                                                                style={{ width: '100%' }}
                                                            >
                                                                {destinationReport.report.appMinIOSVersion && (
                                                                    <Space size={6}>
                                                                        <Popover
                                                                            content={filesPopover}
                                                                            trigger="hover"
                                                                            placement="right"
                                                                            mouseEnterDelay={0.2}
                                                                        >
                                                                            <InfoCircleOutlined
                                                                                style={{
                                                                                    color: rwColors.primary,
                                                                                    cursor: 'help',
                                                                                }}
                                                                            />
                                                                        </Popover>
                                                                        <Text type="secondary">
                                                                            Deployment target:{' '}
                                                                            {formatMinIOS(
                                                                                destinationReport.report
                                                                                    .appMinIOSVersion,
                                                                            )}
                                                                        </Text>
                                                                    </Space>
                                                                )}
                                                                <Table
                                                                    size="middle"
                                                                    rowKey={(row) =>
                                                                        `${destinationReport.destinationId}-${row.identity}-${row.location}`
                                                                    }
                                                                    loading={isLoading}
                                                                    columns={makeDependencyColumns(
                                                                        destinationReport.report.appMinIOSVersion,
                                                                    )}
                                                                    dataSource={destinationReport.report.dependencies}
                                                                    pagination={{
                                                                        defaultPageSize: 25,
                                                                        showSizeChanger: true,
                                                                        pageSizeOptions: [
                                                                            '10',
                                                                            '25',
                                                                            '50',
                                                                            '100',
                                                                            '200',
                                                                        ],
                                                                        showTotal: (t, range) =>
                                                                            `${range[0]}-${range[1]} of ${t}`,
                                                                    }}
                                                                />
                                                            </Space>
                                                        );
                                                    })()
                                                :   <Empty
                                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                        description="No report has been generated for this destination yet"
                                                    />
                                                }
                                            </Collapse.Panel>
                                        );
                                    })}
                                </Collapse>
                            </Space>
                        );
                    })()
                }
            </Space>
            <ConfigureIosDependenciesModal
                open={isModalOpen}
                orgId={orgId}
                appId={appId}
                releaseId={releaseId}
                destinations={modalDestinations}
                initialDestinationId={modalDestinationId}
                existingConfigs={configs}
                onConfigured={handleConfigured}
                onClose={() => setIsModalOpen(false)}
            />
        </Layout.Content>
    );
};

export default IosDependenciesPage;
